import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
    Button,
    Divider,
    Grid,
    Typography,
    useMediaQuery,
    useTheme,
    Paper,
} from '@mui/material';
import dayjs from 'dayjs';

import useEventsData from '../../hooks/useEventsData';
import { selectAllEventTypes } from '../../../../api/eventTypesApiSlice';
import { selectAllCenterNames } from '../../../../api/centersApiSlice';
import { selectAllRegionsObject } from '../../../../api/regionsApiSlice';
import { selectAllAudiences } from '../../../../api/audiencesApiSlice';
import DropdownFilter from '../../../../components/DropdownFilter';
import SearchList from '../../../../components/SearchList';
import ProgressLoader from '../../../../components/ProgressLoader';
import DatePicker from './DatePicker';
import { selectCurrentUser } from '../../../authentication/authSlice';
import { selectAllPeopleNames } from '../../../../api/peopleApiSlice';
import DropdownFilterVastuuhenkilo from '../../../../components/DropDownFilterVastuuhenkilo';
import { useDeleteEventMutation } from '../../../../api/eventsApiSlice';

const ModalContent = () => {
    const { events, finished, setDeletedEventId } = useEventsData({ pageSize: 1000, years: 10 });
    const { role, name } = useSelector(selectCurrentUser);

    const eventTypes = useSelector(selectAllEventTypes);
    const centers = useSelector(selectAllCenterNames);
    const regions = useSelector(selectAllRegionsObject);
    const audiences = useSelector(selectAllAudiences);
    const people = useSelector(selectAllPeopleNames);
    const statuses = ['julkaistu', 'peruutettu', 'raportoitu', 'esitiedot', 'laskutuksessa', 'laskutettu'];

    const dateFormat = 'DD.MM.YYYY';
    const theme = useTheme();

    const isXs = useMediaQuery(theme.breakpoints.down('sm')); // < 600px
    const isSm = useMediaQuery(theme.breakpoints.between('sm', 'md')); // 600px - 960px
    const isMd = useMediaQuery(theme.breakpoints.up('md')); // > 960px

    const defaultFilterState = {
        luonne: '',
        maakunta: '',
        kunta: '',
        keskus: '',
        vastuuhenkilo: '',
        aloitus: null,
        kohdeyleiso: '',
        status: ''
    };
    const [filterState, setFilterState] = useState(defaultFilterState);

    const [deleteEvent] = useDeleteEventMutation();

    const handleFilterChange = (event) => {
        const { name, value } = event.target;
        setFilterState(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const deleteEventById = async (event) => {
        try {
            await deleteEvent(event).unwrap();
            setDeletedEventId(event.id);
        } catch (error) {
            console.log(error);
        }
    };

    const applyFilters = (event) => {
        if (event.status === 'luonnos') return false;
        if (filterState.luonne && !event?.luonteet?.includes(filterState.luonne)) return false;
        if (filterState.vastuuhenkilo && !event?.vastuuhenkilot?.some(person => person.henkilo === filterState.vastuuhenkilo)) return false;
        if (filterState.keskus && event?.paikka?.keskus !== filterState.keskus) return false;
        if (filterState.maakunta && event?.paikka?.maakunta !== filterState.maakunta) return false;
        if (filterState.kunta && event?.paikka?.kunta !== filterState.kunta) return false;
        if (filterState.aloitus && filterState.aloitus.isValid() &&
            dayjs(event?.aloitus, dateFormat).format(dateFormat) !== filterState.aloitus.format(dateFormat)) return false;
        if (filterState.kohdeyleiso && !event?.kohdeyleiso.includes(filterState.kohdeyleiso)) return false;
        if (filterState.status && event?.status !== filterState.status && filterState.status !== '') return false;
        return true;
    };

    const peopleOptions = useMemo(() => [...(people || [])].sort((firstPerson, secondPerson) => firstPerson.localeCompare(secondPerson, 'fi')), [people]);

    const filteredEvents = useMemo(() => events?.filter(applyFilters) ?? [], [events, filterState]);

    const getListDimensions = () => {
        if (isXs) return { height: 280, itemSize: 72 };
        if (isSm) return { height: 340, itemSize: 78 };
        return { height: 420, itemSize: 82 };
    };

    const content = !finished && events.length === 0 ? (
        <Grid
            container
            justifyContent='center'
            alignItems='center'
            sx={{ minHeight: { xs: 280, sm: 340, md: 420 }, textAlign: 'center' }}
        >
            <ProgressLoader msg='Ladataan tapahtumia...' />
        </Grid>
    ) : (
        <SearchList
            hideStatusFilter={true}
            deleteEvent={deleteEventById}
            muotoiltu={true}
            filterTime={true}
            pageSize={20}
            data={filteredEvents}
            title={'Hae tapahtumia'}
            color='taphaBlue.light'
            listDimensions={getListDimensions()}
            primaryPropKey='nimi'
            secondaryPropKey='date.dateString'
        />
    );

    return (
        <Grid
            container
            sx={{
                flexGrow: 1,
                p: { xs: 0.5, sm: 1, md: 1 },
                pt: 0,
                maxWidth: 'xl',
                mx: 'auto',
                height: { xs: 'auto', md: '100%' },
                alignContent: 'stretch',
            }}
        >
            {/* Event List Section */}
            <Grid
                item
                xs={12}
                md={7}
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    pb: { xs: 1.5, md: 0 },
                    minHeight: 0,
                    height: { md: '100%' },
                }}
            >
                <Paper
                    elevation={1}
                    sx={{
                        p: { xs: 1, sm: 1.5 },
                        flexGrow: { md: 1 },
                        borderRadius: 2,
                        bgcolor: 'grey.50',
                        display: 'flex',
                        minHeight: 0,
                        overflow: 'hidden',
                    }}
                >
                    {content}
                </Paper>
            </Grid>

            {/* Filter Section */}
            <Grid
                item
                xs={12}
                md={5}
                sx={{
                    pl: { md: 2 },
                    mt: { xs: 1.5, md: 0 },
                    minHeight: 0,
                    height: { md: '100%' },
                }}
            >
                <Paper
                    elevation={1}
                    sx={{
                        p: { xs: 1, sm: 1.5 },
                        borderRadius: 2,
                        bgcolor: 'grey.50',
                        height: { md: '100%' },
                        overflowY: { md: 'auto' },
                    }}
                >
                    <Grid container spacing={isXs ? 1.5 : 2}>
                        <Grid item xs={12}>
                            <Typography
                                fontFamily='"Bree Serif", serif'
                                textAlign='center'
                                variant={isXs ? 'h6' : 'h5'}
                                color='text.primary'
                                sx={{ fontWeight: 500 }}
                            >
                                Suodattimet
                            </Typography>
                        </Grid>

                        {/* Place Filters */}
                        <Grid item xs={12}>
                            <Divider sx={{ my: 1 }}>
                                <Typography
                                    fontFamily='"Bree Serif", serif'
                                    variant='subtitle2'
                                    color='text.secondary'
                                >
                                    Paikka
                                </Typography>
                            </Divider>
                        </Grid>
                        <Grid item xs={12}>
                            <DropdownFilter
                                label='Keskus'
                                name='keskus'
                                variant='outlined'
                                options={centers}
                                onChange={handleFilterChange}
                                value={filterState.keskus}
                                fullWidth
                                size='small'
                            />
                        </Grid>
                        <Grid item xs={isXs ? 12 : 6}>
                            <DropdownFilter
                                label='Maakunta'
                                name='maakunta'
                                variant='outlined'
                                options={Object.keys(regions)}
                                onChange={handleFilterChange}
                                value={filterState.maakunta}
                                fullWidth
                                size='small'
                            />
                        </Grid>
                        <Grid item xs={isXs ? 12 : 6}>
                            <DropdownFilter
                                label='Kunta'
                                name='kunta'
                                variant='outlined'
                                options={regions[filterState.maakunta] ?? []}
                                onChange={handleFilterChange}
                                value={filterState.kunta}
                                fullWidth
                                size='small'
                            />
                        </Grid>

                        {/* Time Filters */}
                        <Grid item xs={12}>
                            <Divider sx={{ my: 1 }}>
                                <Typography
                                    fontFamily='"Bree Serif", serif'
                                    variant='subtitle2'
                                    color='text.secondary'
                                >
                                    Aika
                                </Typography>
                            </Divider>
                        </Grid>
                        <Grid item xs={12}>
                            <DatePicker
                                value={filterState.aloitus}
                                onChange={(newDate) => setFilterState(prev => ({
                                    ...prev,
                                    aloitus: dayjs(newDate, dateFormat)
                                }))}
                                fullWidth
                                format={dateFormat}
                                variant='outlined'
                                size='small'
                            />
                        </Grid>

                        {/* Other Filters */}
                        <Grid item xs={12}>
                            <Divider sx={{ my: 1 }}>
                                <Typography
                                    fontFamily='"Bree Serif", serif'
                                    variant='subtitle2'
                                    color='text.secondary'
                                >
                                    Muut
                                </Typography>
                            </Divider>
                        </Grid>
                        <Grid item xs={isXs ? 12 : 3}>
                            <DropdownFilter
                                label='Luonne'
                                name='luonne'
                                variant='outlined'
                                options={eventTypes}
                                onChange={handleFilterChange}
                                value={filterState.luonne}
                                fullWidth
                                size='small'
                            />
                        </Grid>
                        <Grid item xs={isXs ? 12 : 3}>
                            <DropdownFilter
                                label='Kohdeyleisö'
                                name='kohdeyleiso'
                                variant='outlined'
                                options={audiences}
                                onChange={handleFilterChange}
                                value={filterState.kohdeyleiso}
                                fullWidth
                                size='small'
                            />
                        </Grid>
                        <Grid item xs={isXs ? 12 : 3}>
                            <DropdownFilterVastuuhenkilo
                                label='Vastuuhenkilö'
                                name='vastuuhenkilo'
                                placeholdervastuuhenkilo=''
                                variant='outlined'
                                options={peopleOptions}
                                onChange={handleFilterChange}
                                value={filterState.vastuuhenkilo}
                                fullWidth
                                size='small'
                            />
                        </Grid>
                        <Grid item xs={isXs ? 12 : 3}>
                            <DropdownFilter
                                label='Status'
                                name='status'
                                variant='outlined'
                                options={statuses}
                                onChange={handleFilterChange}
                                value={filterState.status}
                                fullWidth
                                size='small'
                            />
                        </Grid>

                        <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                            <Button
                                variant='contained'
                                color='primary'
                                sx={{
                                    width: isXs ? '100%' : 'auto',
                                    borderRadius: 1,
                                    textTransform: 'none',
                                    px: 3,
                                    py: 1,
                                    fontWeight: 500,
                                }}
                                onClick={() => setFilterState(defaultFilterState)}
                            >
                                Tyhjennä valinnat
                            </Button>
                        </Grid>
                    </Grid>
                </Paper>
            </Grid>
        </Grid>
    );
};

export default ModalContent;